import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import useMediaSourceViews from '@/actions/useMediaSourceViews'
import GearIcon from '@/CustomIcons/GearIcon'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import { queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import useRoomActions from '@/services/useRoomActions'
import { Button } from '@/UIComponents2/Button'
import MenuButton, { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import { RadioGroup } from '@/UIComponents2/RadioGroup'
import { AudioSourceInfo, useUserContext } from '@/UserContext'
import { useTranslation } from 'react-i18next'
import { sessionSettingsTrigger } from '../../SessionPage'
import { MenuButtonContentTitle } from '@/UIComponents2/MenuButton/MenuButtonContentTitle'

type UserAudioMenuButtonProps = {
    noMenu?: boolean
}
const UserAudioMenuButton = (props: UserAudioMenuButtonProps) => {
    const [userState] = useUserContext()
    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const { t } = useTranslation()

    const { calcIsMyAudioDeviceAllMuted, toggleMyAudioDeviceMute } = useRoomActions()

    const {
        setMediaSelection: changeMediaSelection,
        toggleAudioDeviceSelection,
        setTalkbackDevice,
    } = useMediaSelectionActions()

    const { logUserActivityWithContext } = useAnalytics()

    function handleSelectTalkbackMic(audioInput: AudioSourceInfo) {
        const currentlySelectedTalkbackMic = audioInputsClassifiedForUi.external.find(
            (externalDevice) => externalDevice.deviceId === userState.mediaSelection.talkbackMic
        )

        if (currentlySelectedTalkbackMic && currentlySelectedTalkbackMic.deviceId === audioInput.deviceId) {
            toggleAudioDeviceSelection(currentlySelectedTalkbackMic)
            return
        } else {
            if (currentlySelectedTalkbackMic && currentlySelectedTalkbackMic.deviceId !== audioInput.deviceId) {
                toggleAudioDeviceSelection(currentlySelectedTalkbackMic)
            }

            const isInputAlreadyEnabled = userState.mediaSelection.audioInputs.find(
                (input) => input.deviceId === audioInput.deviceId
            )

            if (isInputAlreadyEnabled) {
                setTimeout(() => {
                    setTalkbackDevice(audioInput.deviceId)
                }, 0)
            } else {
                setTimeout(() => {
                    toggleAudioDeviceSelection(audioInput, { isTalkbackMic: true })
                }, 0)
            }
        }
    }

    const menuContent = !props.noMenu && (
        <div className="flex flex-col gap-[18px]">
            <MenuButtonContentTitle text={t('System Audio')} icon={MicrophoneIcon} />
            <div className="flex flex-col gap-[18px]">
                <RadioGroup
                    groupLabel={t('Talkback Mic')}
                    className="gap-[12px]"
                    labelClassName="opacity-65"
                    items={audioInputsClassifiedForUi.external.map((audioInput) => ({
                        label: audioInput.label,
                        value: audioInput.deviceId,
                    }))}
                    value={userState.mediaSelection.talkbackMic}
                    onChange={(value) => {
                        const audioInput = audioInputsClassifiedForUi.external.find((a) => a.deviceId === value)
                        if (audioInput) {
                            handleSelectTalkbackMic(audioInput)
                        }
                    }}
                />
                <RadioGroup
                    groupLabel={t('System Audio Output')}
                    className="gap-[12px]"
                    labelClassName="opacity-65"
                    items={userState.mediaSources.audioOutputs.map((audioOutput) => ({
                        label: audioOutput.label,
                        value: audioOutput.deviceId,
                    }))}
                    value={queryCurrentMediaSelectionWithFallback(
                        userState.mediaSelection,
                        userState.mediaSources,
                        'audioOutput'
                    )}
                    onChange={(value) => {
                        changeMediaSelection({ audioOutput: value ?? undefined })
                    }}
                />
            </div>
            <MenuItem asChild>
                <Button
                    variant="rectangle"
                    className="text-muse-yellow justify-center"
                    onClick={() => {
                        sessionSettingsTrigger.trigger({ tab: 'audio' })
                    }}
                >
                    <GearIcon /> {t('Audio Settings')}
                </Button>
            </MenuItem>
        </div>
    )

    return (
        <MenuButton
            label={t('Audio')}
            onIconClick={() => {
                toggleMyAudioDeviceMute()
                logUserActivityWithContext(AnalyticsEvents.CLICKED_MUTE_AUDIO_TOGGLE_BUTTON, {
                    toggleState: !calcIsMyAudioDeviceAllMuted(),
                    componentSource: 'VideoGalleryBottomToolbar',
                })
            }}
            icon={
                calcIsMyAudioDeviceAllMuted() ? (
                    <MicrophoneOffIcon className="text-muse-danger" style={{ fontSize: '1.3rem' }} />
                ) : (
                    <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                )
            }
            content={menuContent}
        />
    )
}

export default UserAudioMenuButton
